/**
 * Issues a confirmed import type { ExistingItemRef } from '@/types/procurementImport';
import plan against the API. Never throws for a single
 * failed row: failures are counted so the page can always reload and report.
 */
import { addIngredientVendor } from '@/lib/api/ingredientVendors';
import { createIngredient, fetchIngredients } from '@/lib/api/ingredients';
import { addManualProcurementItem, updateProcurementItem } from '@/lib/api/procurement';
import { getAutoConversionMultiplier } from '@/types/units';
import { roundProcurementQuantity } from '@/components/procurement/helpers';
import { MAX_IMPORT_QUANTITY, type ConfirmPlan } from './confirmPlan';

export interface ImportApi {
  createIngredient: typeof createIngredient;
  fetchIngredients: typeof fetchIngredients;
  addIngredientVendor: typeof addIngredientVendor;
  addManualProcurementItem: typeof addManualProcurementItem;
  updateProcurementItem: typeof updateProcurementItem;
}

const defaultApi: ImportApi = { createIngredient, fetchIngredients, addIngredientVendor, addManualProcurementItem, updateProcurementItem };

export interface ImportOutcome {
  /** Adds and amount updates sent */
  sent: number;
  failed: number;
  /** Recipe rows that could not be excluded */
  exclusionFailures: number;
  createFailures: number;
  /** Reused ingredients whose unit the row could not be converted into, or whose converted amount is too large */
  unitFailures: number;
  vendorFailures: number;
  /** Creates that resolved to an ingredient already on this week's list; nothing was added for them */
  reuseConflicts: number;
  /** List items the plan updated */
  updatedItemIds: number[];
  /** Ingredients that got a MANUAL add or update */
  manualIngredientIds: number[];
}

// A 409 from the library means the name exists (active or not); reuse that ingredient.
async function findExistingIngredient(api: ImportApi, name: string): Promise<{ id: number; defaultUnit: string } | null> {
  const wanted = name.trim().toLowerCase();
  const response = await api.fetchIngredients({ search: name.trim(), activeOnly: 'all', page: 1, pageSize: 50 });
  const match = response.ingredients.find((ingredient) => ingredient.name.trim().toLowerCase() === wanted);
  return match ? { id: match.id, defaultUnit: match.defaultUnit } : null;
}

const settle = (promise: Promise<unknown>) => promise.then(() => true, () => false);

export async function applyImportPlan(plan: ConfirmPlan, listId: number, api: ImportApi = defaultApi, existingItems: ExistingItemRef[] = []): Promise<ImportOutcome> {
  // Ingredients first and in order: the manual add for each new row needs its id.
  const createdIds = new Map<string, number>();
  // Target unit and multiplier for a reused ingredient whose unit differs from the row's.
  const conversions = new Map<string, { unit: string; multiplier: number }>();
  let createFailures = 0;
  let unitFailures = 0;
  let vendorFailures = 0;
  let reuseConflicts = 0;
  const onList = new Set(existingItems.map((item) => item.ingredientId));
  for (const create of plan.creates) {
    let ingredientId: number;
    try {
      ingredientId = (await api.createIngredient(create.request)).id;
    } catch (error) {
      const duplicate = error instanceof Error && error.message.includes('already exists');
      const existing = duplicate ? await findExistingIngredient(api, create.request.name).catch(() => null) : null;
      if (!existing) { createFailures++; continue; }
      // The review planned this as a new ingredient, so it never weighed the rows the list already has for it.
      if (onList.has(existing.id)) { reuseConflicts++; continue; }
      if (existing.defaultUnit !== create.request.defaultUnit) {
        const multiplier = getAutoConversionMultiplier(create.request.defaultUnit, existing.defaultUnit);
        if (multiplier === null) { unitFailures++; continue; }
        conversions.set(create.key, { unit: existing.defaultUnit, multiplier });
      }
      createdIds.set(create.key, existing.id);
      continue;
    }
    createdIds.set(create.key, ingredientId);
    if (create.vendorId !== null) {
      // A failed vendor link is counted but must not block the add below.
      try {
        await api.addIngredientVendor(ingredientId, { vendorId: create.vendorId, isPreferred: true });
      } catch {
        vendorFailures++;
      }
    }
  }

  const manualIngredientIds: number[] = [];
  // Per plan key: the add/update that carries the amount, then any recipe exclusions.
  const amountByKey = new Map<string, Promise<boolean>>();
  for (const add of plan.manualAdds) {
    const ingredientId = add.ingredientId ?? createdIds.get(add.key);
    if (ingredientId === undefined) continue;
    const conversion = conversions.get(add.key);
    // Convert the unrounded sheet total, then round up once in the stored unit.
    const quantity = conversion ? Math.ceil(roundProcurementQuantity((add.rawQuantity ?? add.purchaseQuantity) * conversion.multiplier)) : add.purchaseQuantity;
    if (quantity > MAX_IMPORT_QUANTITY) { unitFailures++; continue; }
    manualIngredientIds.push(ingredientId);
    amountByKey.set(add.key, settle(api.addManualProcurementItem(listId, {
      ingredientId,
      purchaseQuantity: quantity,
      ...(add.vendorId !== undefined ? { vendorId: add.vendorId } : {}),
      // The tag must record the amount in the unit actually stored.
      notes: conversion && add.notes ? add.notes.replace(/^From sheet \([^)]*\):/, `From sheet (+${quantity} ${conversion.unit}):`) : add.notes,
    })));
  }
  for (const update of plan.updates) {
    if (update.purchaseQuantity === undefined) continue;
    amountByKey.set(update.key, settle(api.updateProcurementItem(update.itemId, {
      purchaseQuantity: update.purchaseQuantity,
      ...(update.isExcluded !== undefined ? { isExcluded: update.isExcluded } : {}),
      ...(update.vendorId !== undefined ? { vendorId: update.vendorId } : {}),
      ...(update.notes !== undefined ? { notes: update.notes } : {}),
    })));
  }

  // A recipe row is excluded only once its sheet amount is safely on the list.
  const exclusions = plan.updates.filter((u) => u.purchaseQuantity === undefined).map(async (update) => {
    const amountSaved = await (amountByKey.get(update.key) ?? Promise.resolve(false));
    if (!amountSaved) return 'skipped' as const;
    return (await settle(api.updateProcurementItem(update.itemId, { isExcluded: update.isExcluded ?? true }))) ? 'ok' as const : 'failed' as const;
  });

  const amounts = await Promise.all(amountByKey.values());
  const exclusionResults = await Promise.all(exclusions);
  return {
    sent: amounts.length,
    failed: amounts.filter((ok) => !ok).length,
    exclusionFailures: exclusionResults.filter((r) => r === 'failed').length,
    createFailures,
    unitFailures,
    vendorFailures,
    reuseConflicts,
    updatedItemIds: plan.updates.map((u) => u.itemId),
    manualIngredientIds,
  };
}
